/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {WizardStepComponent} from '../../../shared/wizard/wizard-step.component';
import {WizardService} from '../../../shared/wizard/wizard.service';
import {StepMaterialListModel} from './step-material-list.model';
import {MaterialListService} from '../../../shared/material-list/service/material-list.service';
import {combineLatest, Subscription} from 'rxjs';
import {WizardIconEnum} from '../../../shared/wizard/wizard-icon.enum';
import {startWith} from 'rxjs/operators';
import {SpecialOrderMaterialDetailModel} from '../../../api/ms-specialorders/contracts/special-order-material-detail.model';
import {SpecialOrderService} from '../../../api/ms-specialorders/specialorder/specialorder.service';

@Component({
    selector: 'app-step-material-list',
    templateUrl: './step-material-list.component.html',
    styleUrls: ['./step-material-list.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StepMaterialListComponent extends WizardStepComponent implements OnInit, OnDestroy {

    @Input() stepIndex: number;
    stepModel = {
        data: [],
        canSave: true,
        canSend: false,
        icon: WizardIconEnum.ERROR,
    } as StepMaterialListModel;

    warnings: boolean;
    errors: boolean;

    public maxNumberOfMaterials: string = null;
    private subscriptions = new Subscription();

    constructor(
            private service: WizardService,
            private specialOrderService: SpecialOrderService,
            public materialListService: MaterialListService) {
        super(service);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.getMaxMaterialsConfiguration();
        this.hasWarnings();
        combineLatest([
            this.materialListService.materialData,
            this.materialListService.materialListHasErrors.pipe(startWith(false))
        ]).subscribe(([materials, errors]) => {
            this.stepModel.isTouched = true;
            this.stepModel.data = materials;

            if (errors) {
                this.stepModel.canSend = false;
                this.stepModel.canSave = false;
                this.stepModel.icon = WizardIconEnum.ERROR;
            } else {
                if (this.stepModel.data.length > 0 && this.materialsHaveValidOrderQuantities(materials)) {
                    this.stepModel.canSend = true;
                    this.stepModel.canSave = true;
                    this.warnings ? this.stepModel.icon = WizardIconEnum.WARNING : this.stepModel.icon = WizardIconEnum.SUCCESS;
                } else {
                    this.stepModel.canSend = false;
                    this.stepModel.icon = WizardIconEnum.ERROR;
                }
            }
            this.sendUpdateForm();
        });
    }

    hasWarnings(): void {
        this.subscriptions.add(
                this.materialListService.materialListHasWarnings.subscribe(warnings => this.warnings = warnings));
    }

    /**
     * Checks if all the existing materials in an SpecialOrderMaterialDetailModel have valid order quantities values
     */
    materialsHaveValidOrderQuantities(materials: SpecialOrderMaterialDetailModel[]): boolean {
        return materials.filter(material => material.orderedQuantity == null
                || material.orderedQuantity === 0).length === 0
                && materials.filter(material => material.orderedQuantity.toString() === '').length === 0;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    getMaxMaterialsConfiguration(): void {
        this.subscriptions.add(
                this.specialOrderService.getSpecialOrderConfig().subscribe(maxMaterialsNewConfig =>
                        this.maxNumberOfMaterials = maxMaterialsNewConfig.maxNumberOfMaterials));
    }

}
